import { useCallback } from 'react';
import { useAppStore, recalculateStore } from '@/store/useAppStore';
import { hydrateDemoDataset, DEMO_DATASETS } from '@/data/demoDatasets';
import { useToast } from '@/hooks/useToast';

/**
 * First-run welcome banner shown at the top of the Overview tab when the user
 * has zero courses. Frames the two ways to begin — explore with a sample
 * transcript, or start entering your own — so a brand-new user never lands on
 * an empty form + empty table with no guidance.
 *
 * Renders nothing once at least one course exists (the normal empty-state
 * copy inside `CourseTable` and `GPAComparisonPanel` then carries guidance).
 */
export function OnboardingBanner() {
  const courses = useAppStore((s) => s.courses);
  const loadDemoCourses = useAppStore((s) => s.loadDemoCourses);
  const loadSettings = useAppStore((s) => s.loadSettings);
  const { showToast } = useToast();

  const handleLoadSample = useCallback(() => {
    // First dataset ("First-Year Snapshot") is the gentlest intro — short,
    // freshman-only, no honors complexity. It lets the user see all five GPA
    // variants populate immediately without cognitive load.
    const { courses: demoCourses, settings } = hydrateDemoDataset(0);
    loadDemoCourses(demoCourses);
    loadSettings(settings);
    recalculateStore();
    showToast(`Loaded "${DEMO_DATASETS[0].name}". Edit or delete any course to make it yours.`, 'Dismiss', () => {});
  }, [loadDemoCourses, loadSettings, showToast]);

  const handleScrollToForm = useCallback(() => {
    const form = document.getElementById('course-entry-form');
    if (form) {
      form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Focus the first focusable input inside the form
      const firstInput = form.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), select, textarea, button',
      );
      firstInput?.focus();
    }
  }, []);

  if (courses.length > 0) return null;

  return (
    <section
      aria-labelledby="onboarding-title"
      className="mb-6 rounded-xl border border-primary/30 bg-gradient-to-br from-primary-light to-surface p-6 sm:p-8"
      data-testid="onboarding-banner"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            Start here
          </p>
          <h2
            id="onboarding-title"
            className="mt-2 font-serif text-2xl font-semibold tracking-tight text-[var(--color-text)] sm:text-3xl"
          >
            Welcome. Let&rsquo;s find your real GPA.
          </h2>
          <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
            Add the courses from your transcript and GPA Scales computes the five
            numbers colleges actually ask for — UC Capped, UC Uncapped, UC
            Unweighted, Common App Weighted, and an unweighted baseline. Nothing
            leaves your device.
          </p>
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">
            Two ways to begin: load a sample transcript to see how it works, or
            start entering your own courses below.
          </p>
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:items-end">
          <button
            type="button"
            onClick={handleLoadSample}
            className="rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition-[var(--transition-fast)] hover:bg-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            Load a sample transcript
          </button>
          <button
            type="button"
            onClick={handleScrollToForm}
            className="rounded-md border border-border bg-surface px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] transition-[var(--transition-fast)] hover:border-primary hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            I&rsquo;ll add courses myself
          </button>
        </div>
      </div>
    </section>
  );
}
